import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Container, Spinner } from "react-bootstrap";
import { Navigate, useSearchParams } from "react-router-dom";
import CreateTemplateModal from "@features/report/template/components/CreateTemplateModal";
import { FiPlus } from "react-icons/fi";
import ReportTemplateTable from "../components/ReportTemplateTable";
import { useDepartments } from "@features/department/hooks/useDepartments";
import { useScopeId } from "@core/hooks/useRouteParamId";
import { useEntityBasePath } from "@core/hooks/useEntityBasePath";
import PageBreadcrumb from "@core/components/PageBreadcrumb";
import { departmentsToReportTemplatesTrail } from "@core/navigation/pageBreadcrumbTrails";
import { TEMPLATE_ORIGIN_CREATE_CODES, TEMPLATE_CATALOG_MANAGE_CODES } from '@features/entity/types/actionCodes';
import { usePermissions } from '@features/entity/hooks/usePermissions';

const AdminTemplatesPage: React.FC = () => {
    const [searchParams] = useSearchParams();
    const did = Number(searchParams.get("did")); // department id
    const sid = useScopeId() ?? 0;
    const basePath = useEntityBasePath();
    const breadcrumbItems = useMemo(
        () => (basePath ? departmentsToReportTemplatesTrail(basePath) : []),
        [basePath]
    );
    const [showCreate, setShowCreate] = useState(false);
    const [refreshSignal, setRefreshSignal] = useState(0);
    const [departmentName, setDepartmentName] = useState<string | undefined>(undefined);

    const {
        hasAnyPermission,
        loading: permsLoading,
        error: permsError,
        refresh: refreshPermissions,
    } = usePermissions({
        scopeType: 'NATION',
        scopeId: sid || undefined,
        actionCodes: [...TEMPLATE_CATALOG_MANAGE_CODES],
    });
    const canManageAny = hasAnyPermission([...TEMPLATE_CATALOG_MANAGE_CODES]);
    const canCreateAny = hasAnyPermission([...TEMPLATE_ORIGIN_CREATE_CODES]);

    // Fetch department details (scoped to nation when nationId available)
    const { departments, loading: _loading } = useDepartments({
        departmentId: did,
        ...(sid ? { nationId: sid } : {})
    });

    // Update department name when departments data changes
    useEffect(() => {
        if (departments && departments.length === 1) {
            setDepartmentName(departments[0].name);
        } else {
            setDepartmentName(undefined);
        }
    }, [departments]);

    if (sid > 0 && permsLoading) {
        return (
            <Container fluid className="py-5 d-flex justify-content-center" data-testid="admin-templates-perms-loading">
                <Spinner animation="border" role="status" variant="primary">
                    <span className="visually-hidden">Checking permissions…</span>
                </Spinner>
            </Container>
        );
    }

    if (sid > 0 && permsError) {
        return (
            <Container fluid className="py-4" data-testid="admin-templates-perms-error">
                <Alert variant="danger" className="d-flex flex-column flex-sm-row justify-content-between align-items-sm-center gap-2 mb-0">
                    <span>{permsError}</span>
                    <Button variant="outline-danger" size="sm" onClick={() => refreshPermissions()}>
                        Retry
                    </Button>
                </Alert>
            </Container>
        );
    }

    if (sid > 0 && !canManageAny) {
        return <Navigate to="/entities" replace />;
    }

    return (
        <Container fluid className="py-4" data-testid="admin-templates-page-container">
            <PageBreadcrumb items={breadcrumbItems} data-testid="admin-templates-breadcrumb" />

            <div className="d-flex flex-column flex-md-row justify-content-md-between align-items-md-center gap-3 mb-4" data-testid="admin-templates-page-header-row">
                <div className="ntcog-page-title-block mb-0">
                    <h1 className="ntcog-page-heading mb-1">Report Templates</h1>
                    <div className="d-flex align-items-center gap-2 mt-1">
                        <span className="text-muted small">Department:</span>
                        <span
                            className="badge rounded-pill bg-body-secondary text-body border"
                            title={`Department: ${departmentName || "Unknown"}`}
                            data-testid="department-name"
                        >
                            {(departmentName || "Unknown").toUpperCase()}
                        </span>
                    </div>
                </div>
                {(Number.isFinite(did) && did! > 0) && (Number.isFinite(sid) && sid! > 0) && canCreateAny && (
                    <Button
                        variant="primary"
                        onClick={() => setShowCreate(true)}
                        aria-label="Create a new template"
                        data-testid="new-template-button"
                    >
                        <FiPlus className="me-2" />
                        New Template
                    </Button>
                )}
            </div>

            <ReportTemplateTable
                departmentId={did}
                nationId={sid}
                showPagination={true}
                refreshSignal={refreshSignal}
                data-testid="admin-report-templates-table"
            />

            <CreateTemplateModal
                show={showCreate}
                onHide={() => setShowCreate(false)}
                departmentId={did}
                nationId={sid}
                onChanged={() => setRefreshSignal((s) => s + 1)}
            />
        </Container>
    );
};

export default AdminTemplatesPage;